import { memo, useEffect, useState } from 'react'

import { ChannelResponse } from '@shared/models'

import { Tokens } from '@/assets/tokens'

import { Alert, Api, Confirmation } from '@/modules'

import { Button, Divider, Flex, Switch, TableImage, Tooltip } from '@/components'
import { ColumnType, ContentFilter, SkeletonLoader, Table } from '@/components/derived'

import { useRouting } from '@/hooks'
import { useObserver } from '@/modules/observer'
import { getFromToElements } from '@/utils/pagination'
import { ChannelFilters, useCompanyContext } from '../CompanyDetails.context'

export const ChannelsTable = memo(Component)

const COMPANY_CHANNEL_TABLE = 'company-channel-table'

function Component() {
  const { currentPage, changeQueryParam } = useRouting()
  const { isLoadingChannels, channels, loadChannels } = useCompanyContext()
  const [channelFilter, setChannelFilter] = useState<ChannelFilters>({
    page: currentPage,
    onlyActive: true,
  })
  const { sendMessage } = useObserver()

  const { sendAlert } = Alert.Hook()
  const { open: openConfirmation } = Confirmation.Hook({
    text: 'Tem certeza que deseja desativar este canal?',
    confirmText: 'Sim, desativar',
  })

  const { isRunning: isToggling, run: toggleChannel } = Api.Command<void>(
    Api.Methods.PATCH,
    'channels/:channelID/toggle',
  )

  useEffect(() => {
    loadChannels(channelFilter)
  }, [channelFilter])

  useEffect(() => {
    setChannelFilter({ name: channelFilter?.name, onlyActive: channelFilter?.onlyActive, page: currentPage })
  }, [currentPage])

  function adjustPageIfNeeded(totalAfterAction: number, pageSize: number = 10): void {
    const currentPageNum = Number(currentPage)
    const totalPages = Math.ceil(totalAfterAction / pageSize)

    if (currentPageNum > totalPages && totalPages > 0) {
      changePage(String(totalPages))
    }
  }

  function handleToggle(channel: ChannelResponse, newState: boolean): void {
    const executeToggle = () => {
      toggleChannel<{ isActive: boolean }>({
        binds: [['channelID', channel.id]],
        input: { isActive: newState },
        onSuccess: () => {
          sendAlert({
            type: 'success',
            title: newState ? 'Canal ativado' : 'Canal desativado',
            message: `Canal ${newState ? 'ativado' : 'desativado'} com sucesso`,
          })

          const onlyActive = channelFilter?.onlyActive ?? false
          const willRemoveFromList = (onlyActive && !newState) || (!onlyActive && newState)
          if (willRemoveFromList && channels?.total) {
            adjustPageIfNeeded(channels.total - 1)
          }

          loadChannels(channelFilter)
        },
      })
    }

    if (!newState) {
      openConfirmation(executeToggle)
    } else {
      executeToggle()
    }
  }

  function changePage(page: string): void {
    sendMessage(COMPANY_CHANNEL_TABLE, page)
  }

  function onApplyFilters(channel?: string): void {
    const page = Number(currentPage) > 1 ? '1' : currentPage
    setChannelFilter({ name: channel, onlyActive: channelFilter?.onlyActive, page: page })
    changePage(page)
  }

  function handleEditChannel(channelId: string) {
    changeQueryParam({
      param: 'editChannelId',
      value: channelId,
    })
  }

  const columns: ColumnType<ChannelResponse>[] = [
    {
      key: 'circledLogo',
      width: 100,
      render: (_, item) => <TableImage id={item.circledLogoID} $width="2.25rem" />,
    },
    {
      key: 'name',
      title: 'Nome',
      render: (_, item) => <Tooltip value={item.name} $align="flex-start" length={24} />,
    },
    {
      key: 'tier',
      title: 'Tier',
      width: 70,
    },
    {
      key: 'vehicle',
      title: 'Tipo de mídia',
      width: 110,
      render: (_, item) => (!item.vehicle || item.vehicle === '' ? '--' : item.vehicle),
    },
    {
      key: 'options',
      title: 'Opções',
      align: 'center',
      width: 50,
      render: (_, item) => (
        <Flex $type="row" $justify="flex-start" $align="stretch">
          <Button.Root
            $variant="link"
            style={{ color: Tokens.ColorUIPrimary, marginRight: '-8px' }}
            onClick={() => handleEditChannel(item.id)}
            $loading={isToggling}
            disabled={!item.isActive}>
            Editar
          </Button.Root>
        </Flex>
      ),
    },
    {
      key: 'active',
      title: 'Ativo',
      align: 'center',
      width: 50,
      render: (_, item) => {
        const handleSwitchToggle = () => {
          const newState = !item.isActive
          handleToggle(item, newState)
        }
        return <Switch.Root isChecked={item.isActive ?? true} toggle={handleSwitchToggle} />
      },
    },
  ]

  return (
    <>
      <Divider.Root>
        <Divider.Title value="Canais registrados" />
        <Divider.Subtitle
          value={`${getFromToElements(currentPage, channels?.elements.length ?? 0)}/${channels?.total ?? 0} Entrada(s)`}
        />
      </Divider.Root>

      <Divider.Root>
        <ContentFilter
          isLoading={isLoadingChannels}
          onApply={onApplyFilters}
          onReset={onApplyFilters}
          placeholder={'Canal'}>
          <Switch.Root
            label="Apenas ativos"
            isChecked={channelFilter?.onlyActive ?? true}
            toggle={() => {
              const newValue = !channelFilter?.onlyActive
              setChannelFilter({ ...channelFilter, onlyActive: newValue, page: '1' })
              changePage('1')
            }}
            direction="row"
          />
        </ContentFilter>
      </Divider.Root>

      {isLoadingChannels ?
        <SkeletonLoader amount={10} />
      : <Table
          name={COMPANY_CHANNEL_TABLE}
          isLoading={isLoadingChannels}
          columns={columns}
          data={channels?.elements}
          totalPages={channels?.pages}
        />
      }
    </>
  )
}
